import React, { useState } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  useMutation,
  useQuery,
  createHttpLink,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { Pinpad } from "./components/Pinpad";
import { QRScanner } from "./components/QRScanner";
import { EventTypeSelector } from "./components/EventTypeSelector";
import { EventConfirmation } from "./components/EventConfirmation";
import DeviceRegistration from "./components/DeviceRegistration";
import { Toaster } from "./components/ui/toaster";
import { useToast } from "./hooks/use-toast";
import { useDeviceVerification } from "./hooks/useDeviceVerification";
import {
  LOGIN_MUTATION,
  LOGIN_WITH_QR_MUTATION,
  LOGOUT_MUTATION,
  GET_EVENT_TYPES,
  CREATE_EVENT_MUTATION,
  GET_EMPLOYEE_CURRENT_STATE,
} from "./graphql/queries";

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || "http://localhost:3000/graphql",
  credentials: 'include', // This ensures cookies are sent with requests
});

const authLink = setContext((_, { headers }) => {
  // Device JWT is in sessionStorage for device authentication
  const deviceToken = sessionStorage.getItem("workflow_device_jwt");
  
  // User authentication is now handled via httpOnly cookies automatically
  // Only add Authorization header if we have a device token
  return {
    headers: {
      ...headers,
      ...(deviceToken && { authorization: `Bearer ${deviceToken}` }),
    },
  };
});

const client = new ApolloClient({
  link: from([authLink, httpLink]),
  cache: new InMemoryCache(),
});

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface EventType {
  id: string;
  name: string;
  code: string;
  description?: string;
  color: string;
  icon?: string;
  order: number;
}

interface CreatedEvent {
  id: string;
  timestamp: string;
  employee: {
    firstName: string;
    lastName: string;
  };
  eventType: {
    name: string;
    color: string;
  };
}

type AppState = "login" | "selectEvent" | "confirmation";
type LoginMode = "pin" | "qr";

function TabletApp() {
  const [state, setState] = useState<AppState>("login");
  const [loginMode, setLoginMode] = useState<LoginMode>("pin");
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(
    null
  );
  const [createdEvent, setCreatedEvent] = useState<CreatedEvent | null>(null);
  const { toast } = useToast();
  
  // Device verification hook
  const {
    isVerified,
    isLoading: deviceLoading,
    error: deviceError,
    verificationResult,
    retryVerification,
  } = useDeviceVerification();

  const [login, { loading: loginLoading }] = useMutation(LOGIN_MUTATION);
  const [loginWithQR, { loading: loginWithQRLoading }] = useMutation(LOGIN_WITH_QR_MUTATION);
  const [logout] = useMutation(LOGOUT_MUTATION);
  const [createEvent, { loading: createEventLoading }] = useMutation(
    CREATE_EVENT_MUTATION
  );

  const { data: eventTypesData, loading: eventTypesLoading } =
    useQuery(GET_EVENT_TYPES);
  const {
    data: employeeStateData,
    loading: employeeStateLoading,
    refetch: refetchEmployeeState,
  } = useQuery(GET_EMPLOYEE_CURRENT_STATE, {
    variables: { employeeId: employee?.id },
    skip: !employee?.id,
  });

  const handleLogin = async (pin: string) => {
    try {
      const { data } = await login({ variables: { pin } });
      if (data?.login?.employee) {
        setEmployee(data.login.employee);
        // Token is now stored in httpOnly cookie automatically
        setState("selectEvent");
        // Refetch employee state after login
        setTimeout(() => refetchEmployeeState(), 100);
      }
    } catch (error: any) {
      toast({
        title: "Błąd logowania",
        description: error.message || "Nieprawidłowy PIN",
        variant: "destructive",
      });
    }
  };

  const handleQRLogin = async (qrData: string) => {
    try {
      const { data } = await loginWithQR({ variables: { qrData } });
      if (data?.loginWithQR?.employee) {
        setEmployee(data.loginWithQR.employee);
        // Token is now stored in httpOnly cookie automatically
        setState("selectEvent");
        setLoginMode("pin"); // Reset to PIN mode after successful login
        // Refetch employee state after login
        setTimeout(() => refetchEmployeeState(), 100);
      }
    } catch (error: any) {
      toast({
        title: "Błąd logowania przez QR",
        description: error.message || "Nieprawidłowy kod QR",
        variant: "destructive",
      });
    }
  };

  const handleSelectEventType = async (eventTypeId: string) => {
    if (!employee) return;

    try {
      const { data } = await createEvent({
        variables: {
          employeeId: employee.id,
          eventTypeId: eventTypeId,
        },
      });

      if (data?.createEvent) {
        setCreatedEvent(data.createEvent);
        setState("confirmation");
        // Refetch employee state after creating event
        setTimeout(() => refetchEmployeeState(), 100);
      }
    } catch (error: any) {
      toast({
        title: "Błąd rejestracji zdarzenia",
        description: error.message || "Nie udało się zarejestrować zdarzenia",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    // Call backend logout to clear httpOnly cookie
    try {
      await logout();
    } catch (e) {
      // Ignore logout errors - we still want to reset the UI
    }
    setEmployee(null);
    setSelectedEventType(null);
    setCreatedEvent(null);
    setState("login");
  };

  const handleCancel = async () => {
    // Call backend logout to clear httpOnly cookie
    try {
      await logout();
    } catch (e) {
      // Ignore logout errors
    }
    setState("login");
    setEmployee(null);
    setLoginMode("pin"); // Reset to PIN mode
  };

  const eventTypes = eventTypesData?.eventTypes || [];
  const employeeState = employeeStateData?.employeeCurrentState;

  // Show device registration/verification screen if device is not verified
  if (!isVerified && !deviceLoading) {
    return (
      <div className="min-h-screen bg-background">
        <DeviceRegistration
          onDeviceVerified={(device: any) => {
            console.log('Device verified:', device);
            toast({
              title: "Urządzenie zatwierdzone",
              description: `Urządzenie ${device.deviceName} zostało zatwierdzone`,
              variant: "default",
            });
          }}
          onRegistrationComplete={() => {
            // Retry verification after registration
            retryVerification();
          }}
          onStatusUpdated={() => {
            // Retry verification after status update
            retryVerification();
          }}
        />
        <Toaster />
      </div>
    );
  }

  // Show loading screen during device verification
  if (deviceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Sprawdzanie urządzenia...</h2>
          <p className="text-muted-foreground">Weryfikujemy uprawnienia urządzenia</p>
        </div>
        <Toaster />
      </div>
    );
  }

  // Show device error screen
  if (deviceError && !isVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Błąd weryfikacji urządzenia</h2>
          <p className="text-muted-foreground mb-4">{deviceError}</p>
          <button
            onClick={() => retryVerification()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Spróbuj ponownie
          </button>
        </div>
        <Toaster />
      </div>
    );
  }

  // Main app content (only shown if device is verified)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">WorkFlow</h1>
          <p className="text-lg text-gray-600">
            System rejestracji czasu pracy
          </p>
          {verificationResult?.device && (
            <p className="text-sm text-gray-500 mt-2">
              Urządzenie: {verificationResult.device.deviceName}
            </p>
          )}
        </div>

        {/* Main Content */}
        {state === "login" && loginMode === "pin" && (
          <Pinpad
            onSubmit={handleLogin}
            onSwitchToQR={() => setLoginMode("qr")}
            isLoading={loginLoading}
          />
        )}

        {state === "login" && loginMode === "qr" && (
          <QRScanner
            onScanSuccess={handleQRLogin}
            onClose={() => setLoginMode("pin")}
            isLoading={loginWithQRLoading}
          />
        )}

        {state === "selectEvent" && employee && (
          <EventTypeSelector
            employee={employee}
            eventTypes={eventTypes}
            onSelectEventType={handleSelectEventType}
            isLoading={
              createEventLoading || eventTypesLoading || employeeStateLoading
            }
            onCancel={handleCancel}
            availableActions={employeeState?.availableActions || []}
            currentState={employeeState?.currentState || "OUTSIDE_WORK"}
            autoBreak={employeeState?.autoBreak || false}
          />
        )}

        {state === "confirmation" && createdEvent && (
          <EventConfirmation
            employee={{
              id: employee?.id || "",
              firstName: createdEvent.employee.firstName,
              lastName: createdEvent.employee.lastName,
            }}
            eventType={{
              id: "",
              name: createdEvent.eventType.name,
              color: createdEvent.eventType.color,
            }}
            timestamp={new Date(createdEvent.timestamp)}
            onClose={handleLogout}
            autoCloseDelay={4000}
          />
        )}
      </div>

      <Toaster />
    </div>
  );
}

function App() {
  return (
    <ApolloProvider client={client}>
      <TabletApp />
    </ApolloProvider>
  );
}

export default App;
